import React from "react";

interface ChatMessageProps {
	message: string | React.ReactNode;
	isUser: boolean;
}

// Improved ChatMessage component with better formatting
const ChatMessage = ({ message, isUser }: ChatMessageProps) => {
	// Helper function to format lists and paragraphs
	const formatMessage = (text: string) => {
		if (!text) return "";

		// Split by number list markers (e.g., "1. ", "2. ")
		const parts = text.split(/(\d+\.\s)/);

		if (parts.length <= 1) {
			// No numbered list found, just return the text with line breaks as <br>
			return text.split("\n").map((line: string, i: number) => (
				<React.Fragment key={`line-${i}-${line.substring(0, 10)}`}>
					{line}
					{i < text.split("\n").length - 1 && <br />}
				</React.Fragment>
			));
		}

		// Process text with numbered lists
		const result = [];
		let currentList = null;
		let itemCount = 0;

		for (let i = 0; i < parts.length; i++) {
			// Check if this part is a number marker
			if (/^\d+\.\s$/.test(parts[i])) {
				// If we're not already in a list, start a new one
				if (currentList === null) {
					currentList = [];
					itemCount = 0;
				}

				// Add the list item (which is the next part)
				if (i + 1 < parts.length) {
					currentList.push(parts[i] + parts[i + 1]);
					i++; // Skip the next part as we've already used it
					itemCount++;
				}
			} else {
				// If we have a list built up, add it to the result
				if (currentList !== null && currentList.length > 0) {
					result.push(
						<ul
							key={`list-${result.length}-${currentList[0].substring(0, 10)}`}
							className="list-decimal pl-5 my-2"
						>
							{currentList.map((item: string, idx: number) => (
								<li
									key={`item-${idx}-${item.substring(0, 10)}`}
									className="mb-1"
								>
									{item.replace(/^\d+\.\s/, "")}
								</li>
							))}
						</ul>,
					);
					currentList = null;
				}

				// Add the current part if it's not empty
				if (parts[i].trim()) {
					result.push(
						<p
							key={`p-${result.length}-${parts[i].substring(0, 10)}`}
							className="mb-2"
						>
							{parts[i].split("\n").map((line: string, j: number) => (
								<React.Fragment key={`line-${j}-${line.substring(0, 10)}`}>
									{line}
									{j < parts[i].split("\n").length - 1 && <br />}
								</React.Fragment>
							))}
						</p>,
					);
				}
			}
		}

		// If we still have a list at the end, add it
		if (currentList !== null && currentList.length > 0) {
			result.push(
				<ul
					key={`list-${result.length}-${currentList[0].substring(0, 10)}`}
					className="list-decimal pl-5 my-2"
				>
					{currentList.map((item: string, idx: number) => (
						<li key={`item-${idx}-${item.substring(0, 10)}`} className="mb-1">
							{item.replace(/^\d+\.\s/, "")}
						</li>
					))}
				</ul>,
			);
		}

		return result;
	};

	return (
		<div className={`flex ${isUser ? "justify-end" : "justify-start"} mb-3`}>
			<div
				className={`max-w-[80%] rounded-2xl px-4 py-2 break-words ${
					isUser ? "bg-primary text-white" : "bg-secondary text-white"
				}`}
			>
				{typeof message === "string" ? formatMessage(message) : message}
			</div>
		</div>
	);
};

export default ChatMessage;
